import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CaUpload from './ca_upload';
import CaCopy from './ca_copy';
import ManagedInput from 'components/form/managed_input';

import { faFilePdf, faFileWord } from '@fortawesome/pro-solid-svg-icons';
import { Panel } from 'react-bootstrap';

const PanelHeader = ({ checked, title, onChange }) => (
  <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
    <Panel.Title>
      {title}
    </Panel.Title>
    <input
      checked={checked}
      className="larger-radio-button mt-0"
      type="radio"
      value="custom_text"
      onChange={onChange}
    />
  </div>
);

PanelHeader.propTypes = {
  checked: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};
export default function ConfidentialityAgreementForm({
  allowDigitalSignPdfCas,
  caData,
  caOptions,
  caUrl,
  roleLabel,
  show,
  standardText,
  onCaUpload,
  onSimpleFieldChange
}) {
  if (!show) return null;
  // customCaEnabled is a boolean used to determine if the Digital Sign area should show standard CA or custom
  const [customCaEnabled, setCustomCaEnabled] = useState(caData.caType === 'custom_text');

  useEffect(() => {
    // This hook allows the user to toggle between standard and custom CA types without changing the actual caType
    if (customCaEnabled && caData.caType === 'standard') {
      onSimpleFieldChange('caType', 'custom_text');
    } else if (!customCaEnabled && caData.caType === 'custom_text') {
      onSimpleFieldChange('caType', 'standard');
    }
  }, [customCaEnabled]);

  function digitalSignUploadZone() {
    if (!allowDigitalSignPdfCas) return null;

    const fileRemoved = () => onCaUpload({
      digitalSignUploadId: null, digitalSignFileFileName: null, digitalSignFileUpdatedAt: ''
    });
    const onFileUploaded = (upload) => {
      const dataToPass = {
        digitalSignUploadId: upload.id,
        digitalSignFileFileName: upload.file_name,
        digitalSignFileUpdatedAt: 'Just now'
      };
      onCaUpload(dataToPass);
    };

    return (
      <div className="mt-4">
        <CaUpload
          acceptedFileTypes={['application/pdf']}
          caUrl={caUrl}
          icon={faFilePdf}
          key="digital_upload_pdf"
          uploadedAgo={caData.digitalSignFileUpdatedAt}
          uploadedFile={caData.digitalSignFileFileName}
          onFileRemove={fileRemoved}
          onFileUploaded={onFileUploaded}
        />
      </div>
    );
  }

  const findCaOption = (key) => {
    return caOptions.find(option => option[1] === key);
  };

  function renderTextCaCopy() {
    if (!customCaEnabled) return (
      <CaCopy
        key="standard"
        readOnly={true}
        text={standardText}
      />
    );

    const onCopyClick = () => onSimpleFieldChange('text', standardText);
    const onTextChange = newValue => onSimpleFieldChange('text', newValue);
    return (
      <React.Fragment>
        <CaCopy
          key="custom_text"
          readOnly={false}
          text={caData.text}
          onCopy={onCopyClick}
          onTextChange={onTextChange}
        />
        {digitalSignUploadZone()}
      </React.Fragment>
    );
  }

  const renderDigitalSignPanel = () => {

    return (
      <Panel className="flex-1">
        <PanelHeader
          checked={['custom_text', 'standard'].includes(caData.caType)}
          title="Digital Sign CA"
          onChange={() => onSimpleFieldChange('caType', 'custom_text')}
        />
        <Panel.Body>
          {renderTextCaCopy()}
          <div>
            <ManagedInput
              className="mt-3 form-control"
              id="ca_type_selection"
              type="select"
              value={customCaEnabled ? 'custom_text' : 'standard'}
              onValueChange={value => setCustomCaEnabled(value === 'custom_text')}
            >
              <option value="standard">Buildout Default</option>
              <option value="custom_text">Custom CA</option>
            </ManagedInput>
          </div>
        </Panel.Body>
      </Panel>
    );
  };

  const renderPdfCAPanel = () => {
    if (!findCaOption('custom_pdf')) return null;

    const fileRemoved = () => onCaUpload({ uploadId: null, fileFileName: null, fileUpdatedAt: '' });
    const onFileUploaded = (upload) => {
      const dataToPass = { uploadId: upload.id, fileFileName: upload.file_name, fileUpdatedAt: 'Just now' };
      onCaUpload(dataToPass);
    };

    return (
      <Panel className="flex-1">
        <PanelHeader
          checked={caData.caType === 'custom_pdf'}
          title="PDF CA"
          onChange={() => onSimpleFieldChange('caType', 'custom_pdf')}
        />
        <Panel.Body>
          <CaUpload
            acceptedFileTypes={['application/pdf']}
            caUrl={caUrl}
            icon={faFilePdf}
            key="custom_pdf"
            uploadedAgo={caData.fileUpdatedAt}
            uploadedFile={caData.fileFileName}
            onFileRemove={fileRemoved}
            onFileUploaded={onFileUploaded}
          />
        </Panel.Body>
      </Panel>
    );
  };

  const renderWordCAPanel = () => {
    if (!findCaOption('custom_word')) return null;

    const fileRemoved = () => onCaUpload({ wordUploadId: null, wordFileFileName: null, wordFileUpdatedAt: '' });
    const onFileUploaded = (upload) => {
      const dataToPass = {
        wordUploadId: upload.id,
        wordFileFileName: upload.file_name,
        wordFileUpdatedAt: 'Just now'
      };
      onCaUpload(dataToPass);
    };

    return (
      <Panel className="flex-1">
        <PanelHeader
          checked={caData.caType === 'custom_word'}
          title="Word CA"
          onChange={() => onSimpleFieldChange('caType', 'custom_word')}
        />
        <Panel.Body>
          <CaUpload
            acceptedFileTypes={['application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
            caUrl={caUrl}
            icon={faFileWord}
            key="custom_word"
            uploadedAgo={caData.wordFileUpdatedAt}
            uploadedFile={caData.wordFileFileName}
            onFileRemove={fileRemoved}
            onFileUploaded={onFileUploaded}
          />
        </Panel.Body>
      </Panel>
    );
  };

  const showSecondColumn = useMemo(() => {
    return caOptions.some(option => ['custom_pdf', 'custom_word'].includes(option[1]));
  }, [caOptions]);

  return (
    <Panel className="p-4 mt-4 d-flex flex-column gap-4">
      <h4 className="m-0 fw-bold">{roleLabel} Confidentiality Agreement</h4>

      <div className="d-flex align-items-stretch gap-2">
        {renderDigitalSignPanel()}
        {showSecondColumn && (
          <div className="flex-1 d-flex gap-2 flex-column">
            {renderPdfCAPanel()}
            {renderWordCAPanel()}
          </div>
        )}
      </div>
    </Panel>
  );
}

export const CA_DATA_SHAPE = {
  id: PropTypes.number.isRequired,
  caType: PropTypes.string,
  digitalSignFileFileName: PropTypes.string,
  digitalSignFileUpdatedAt: PropTypes.string,
  text: PropTypes.string,
  fileFileName: PropTypes.string,
  fileUpdatedAt: PropTypes.string,
  wordFileFileName: PropTypes.string,
  wordFileUpdatedAt: PropTypes.string
};

ConfidentialityAgreementForm.propTypes = {
  allowDigitalSignPdfCas: PropTypes.bool.isRequired,
  caData: PropTypes.shape(CA_DATA_SHAPE),
  caOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
  caUrl: PropTypes.string.isRequired,
  roleLabel: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  standardText: PropTypes.string.isRequired,
  onCaUpload: PropTypes.func.isRequired,
  onSimpleFieldChange: PropTypes.func.isRequired
};
